import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import type { FormikHelpers } from 'formik';
import { AI_WALLET_INSUFFICIENT_CODE } from '@/app/common/components/AiInsufficientBalanceModal';
import { useSendContentAnalysisMutation } from '@/app/ContentTools/api/aiContentAnalysisApi';
import type { AiAnalysisFormValues } from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisForm';
import { aiAnalysisDetailsPath } from '@/app/ContentTools/utils/aiAnalysisPaths';
import {
  AI_NO_ACTIVE_PROVIDER_KEY_CODE,
  resolveAiFormAlertMessage
} from '@/app/common/validators/aiMessages';
import walletApi from '@/app/Wallet/api/walletApi';
import { successMessage } from '@/utils/toast';

type ApiErrorResponse = {
  status?: number;
  data?: {
    code?: number;
    msg?: string;
  };
};

const getResultTab = (values: AiAnalysisFormValues) => {
  const selectedCount = [
    values.analyzeSeo,
    values.analyzeAi,
    values.analyzeEeat,
    values.analyzeGeo
  ].filter(Boolean).length;
  if (selectedCount > 1) return 'overview' as const;
  if (values.analyzeGeo) return 'geo' as const;
  if (values.analyzeEeat) return 'eeat' as const;
  if (values.analyzeSeo) return 'seo' as const;
  if (values.analyzeAi) return 'detector' as const;
  return 'overview' as const;
};

const useSendAiAnalysisForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { domain_slug, domain } = useParams();
  const domainSlug = domain_slug || '';
  const currentDomain = domain || '';
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] =
    useState(false);
  const [showNoActiveApiKeyAlert, setShowNoActiveApiKeyAlert] = useState(false);
  const [formWarningMessage, setFormWarningMessage] = useState<string | null>(
    null
  );
  const [sendContentAnalysis, { isLoading: isSubmitting }] =
    useSendContentAnalysisMutation();

  const onSubmit = async (
    values: AiAnalysisFormValues,
    actions: FormikHelpers<AiAnalysisFormValues>
  ) => {
    setShowNoActiveApiKeyAlert(false);
    setFormWarningMessage(null);
    try {
      const needsSeoFields = values.analyzeSeo;
      const res = await sendContentAnalysis({
        domain_slug: domainSlug,
        keyword: needsSeoFields ? values.keyword.trim() : null,
        goal: needsSeoFields ? values.goal.trim() : null,
        analysis_types: [
          ...(values.analyzeSeo ? (['seo_analysis'] as const) : []),
          ...(values.analyzeAi ? (['ai_detection'] as const) : []),
          ...(values.analyzeEeat ? (['eeat_analysis'] as const) : []),
          ...(values.analyzeGeo ? (['ai_search'] as const) : [])
        ],
        ...(values.source === 'content'
          ? { content: values.content.trim() }
          : { url: values.url.trim() })
      }).unwrap();
      if (res?.code === 200) {
        if (res?.data?.error?.code !== null) {
          setFormWarningMessage(
            res?.data?.error?.message ||
              'در ارتباط با سرویس تحلیل AI خطایی رخ داد.'
          );
          return;
        }
        if (res?.data?.id) {
          successMessage('درخواست تحلیل با موفقیت ثبت شد');
          navigate(
            aiAnalysisDetailsPath(
              getResultTab(values),
              domainSlug,
              currentDomain,
              res.data.id
            )
          );
          actions.resetForm();
          dispatch(walletApi.util.invalidateTags(['Wallet']));
        }
        return;
      }
      if (res?.code === AI_WALLET_INSUFFICIENT_CODE) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      if (res?.code === AI_NO_ACTIVE_PROVIDER_KEY_CODE) {
        setShowNoActiveApiKeyAlert(true);
        return;
      }
      setFormWarningMessage(
        resolveAiFormAlertMessage(
          res?.code,
          res?.msg,
          'در ارتباط با سرویس تحلیل AI خطایی رخ داد.'
        )
      );
    } catch (err) {
      const error = err as ApiErrorResponse;
      if (error?.data?.code === AI_WALLET_INSUFFICIENT_CODE) {
        setShowInsufficientBalanceModal(true);
        return;
      }
      if (error?.data?.code === AI_NO_ACTIVE_PROVIDER_KEY_CODE) {
        setShowNoActiveApiKeyAlert(true);
        return;
      }
      if (error?.status === 500 && error?.data?.code === 0) {
        return;
      }
      setFormWarningMessage(
        resolveAiFormAlertMessage(
          error?.data?.code,
          error?.data?.msg,
          'در ارتباط با سرویس تحلیل AI خطایی رخ داد. لطفا دوباره تلاش کنید.'
        )
      );
    } finally {
      actions.setSubmitting(false);
    }
  };

  return {
    onSubmit,
    isSubmitting,
    showInsufficientBalanceModal,
    setShowInsufficientBalanceModal,
    showNoActiveApiKeyAlert,
    setShowNoActiveApiKeyAlert,
    formWarningMessage,
    setFormWarningMessage
  };
};

export default useSendAiAnalysisForm;
